// Importação do borderô da Prefeitura pelo Financeiro (#6208 / 6208.06).
//
// A edge function `separar-bordero` abre o .7z, separa uma fatura por página e diz o
// destino de cada uma (lançar / acompanhamento / revisar). As páginas "lançar" seguem,
// uma a uma, pelo MESMO webhook do "Importar fatura em PDF" (Robô Manual no n8n). Aqui
// fica a parte pura: o que cada resposta do robô significa e o resumo do envio.
//
// O n8n pode responder 200 com corpo vazio quando o fluxo falha antes do "respond"
// (ver CLAUDE.md), então resposta boa do robô não basta: a página só conta como lançada
// se a fatura aparecer no banco depois do envio.
//
// Roda em `node --test`:
//   node --experimental-strip-types --test src/lib/__tests__/importacaoBordero.test.ts

import { ehErroDuplicataFatura, faturaEntraNaDuplicata } from './duplicataFatura.ts';

export type DestinoPagina = 'lancar' | 'acompanhamento' | 'revisar';

/** Uma fatura como a `separar-bordero` devolve (quase sempre uma página do arquivo). */
export interface PaginaBordero {
  ordem: number;
  pdf: string;
  /** Primeira página da fatura no arquivo. */
  pagina: number;
  /** Todas as páginas da fatura: a de média tensão tem 2 ou 3. Ausente em resposta antiga da edge. */
  paginas?: number[];
  arquivo_nome: string;
  caminho: string;
  url: string;
  bytes: number;
  uc: string | null;
  uc_norm: string | null;
  mes_referencia: string | null;
  total: number | null;
  tem_rateio: boolean;
  agrupamento: { id: number; nome: string } | null;
  destino: DestinoPagina;
  motivo: string | null;
  avisos: string[];
}

export interface SeparacaoBordero {
  ok: true;
  lote: string;
  arquivo: { nome: string | null; tipo: '7z' | 'pdf'; bytes: number; caminho: string; pdfs: string[]; ignorados_no_7z: string[] };
  resumo: { faturas: number; lancar: number; acompanhamento: number; revisar: number; em_branco: number; total_reais: number };
  paginas: PaginaBordero[];
  em_branco: { pdf: string; pagina: number }[];
}

export type EstadoEnvio = 'aguardando' | 'ja_existia' | 'enviando' | 'lancada' | 'falhou';

export interface ResultadoEnvio {
  estado: EstadoEnvio;
  mensagem?: string;
}

/** "11" ou "11–12": as páginas do arquivo que formam a fatura. */
export function rotuloPaginas(p: Pick<PaginaBordero, 'pagina' | 'paginas'>): string {
  const todas = p.paginas?.length ? p.paginas : [p.pagina];
  return todas.length > 1 ? `${todas[0]}–${todas[todas.length - 1]}` : String(todas[0]);
}

/** Mesma chave que o banco usa para recusar duplicata: UC normalizada + mês. */
export const chaveUcMes = (ucNorm: string | null, mes: string | null) => `${ucNorm ?? ''}|${mes ?? ''}`;

interface FaturaDoMes {
  uc_codigo_norm: string | null;
  mes_referencia: string | null;
  classificacao?: string | null;
  status_fatura?: string | null;
}

/** UC+mês que já têm fatura ativa — essas páginas nem vão para a leitura. */
export function chavesComFatura(faturas: FaturaDoMes[]): Set<string> {
  return new Set(
    faturas.filter((f) => faturaEntraNaDuplicata(f)).map((f) => chaveUcMes(f.uc_codigo_norm, f.mes_referencia)),
  );
}

export type RespostaRobo = { tipo: 'ok' } | { tipo: 'duplicata'; mensagem: string } | { tipo: 'erro'; mensagem: string };

/** O que o webhook do Robô Manual respondeu (mesma leitura do "Importar fatura em PDF"). */
export function interpretarRespostaRobo(http: { ok: boolean; status: number }, corpo: unknown): RespostaRobo {
  const c = (corpo && typeof corpo === 'object' ? corpo : {}) as Record<string, any>;
  if (http.ok && c.success !== false) return { tipo: 'ok' };
  const mensagem = String(c.error || c.message || `O robô respondeu com erro (HTTP ${http.status}).`);
  return ehErroDuplicataFatura(mensagem) ? { tipo: 'duplicata', mensagem } : { tipo: 'erro', mensagem };
}

/** Resultado final de uma página: a resposta do robô conferida com o banco. */
export function resultadoDoEnvio(resposta: RespostaRobo, faturaApareceu: boolean): ResultadoEnvio {
  if (resposta.tipo === 'duplicata') return { estado: 'ja_existia', mensagem: 'Já havia fatura desta UC no mês.' };
  if (resposta.tipo === 'erro') return { estado: 'falhou', mensagem: resposta.mensagem };
  if (faturaApareceu) return { estado: 'lancada' };
  return { estado: 'falhou', mensagem: 'O robô respondeu, mas a fatura não apareceu no sistema. Tente de novo esta página.' };
}

export function resumoDoEnvio(resultados: ResultadoEnvio[]) {
  const conta = (e: EstadoEnvio) => resultados.filter((r) => r.estado === e).length;
  return {
    lancadas: conta('lancada'),
    ja_existiam: conta('ja_existia'),
    falharam: conta('falhou'),
    pendentes: conta('aguardando') + conta('enviando'),
  };
}

// ─── De qual agrupamento é o borderô ─────────────────────────────────────────────────────────
// O arquivo não diz de qual Secretaria é (o código do agrupamento vem só no assunto do e-mail).
// Quem diz é o cadastro: cada página já volta da `separar-bordero` com o agrupamento da UC.

export interface AgrupamentoNoBordero {
  id: number;
  nome: string;
  /** Quantas UCs do arquivo estão neste agrupamento (UC com duas páginas conta uma vez). */
  faturas: number;
}

export interface IdentificacaoBordero {
  /** Agrupamento com mais UCs no arquivo. null = nenhuma UC do arquivo está em agrupamento. */
  principal: AgrupamentoNoBordero | null;
  /** Outros agrupamentos que também aparecem: sinal de cadastro a conferir. */
  outros: AgrupamentoNoBordero[];
  /** UCs lidas que não estão em nenhum agrupamento da Vigor (ficam em acompanhamento). */
  foraDeAgrupamento: number;
  /** UCs lidas no arquivo. */
  faturasComUc: number;
}

export function identificarAgrupamento(paginas: Pick<PaginaBordero, 'uc_norm' | 'agrupamento'>[]): IdentificacaoBordero {
  const porUc = new Map<string, PaginaBordero['agrupamento']>();
  for (const p of paginas) {
    if (!p.uc_norm) continue;
    // Se a UC aparece em mais de uma página, vale a que trouxer agrupamento.
    if (!porUc.get(p.uc_norm)) porUc.set(p.uc_norm, p.agrupamento ?? null);
  }
  const contagem = new Map<number, AgrupamentoNoBordero>();
  let foraDeAgrupamento = 0;
  for (const agr of porUc.values()) {
    if (!agr) { foraDeAgrupamento++; continue; }
    const atual = contagem.get(agr.id);
    if (atual) atual.faturas++;
    else contagem.set(agr.id, { id: agr.id, nome: agr.nome, faturas: 1 });
  }
  const ordenados = [...contagem.values()].sort((a, b) => b.faturas - a.faturas || a.id - b.id);
  return { principal: ordenados[0] ?? null, outros: ordenados.slice(1), foraDeAgrupamento, faturasComUc: porUc.size };
}

const soDigitosSemZeros = (v: string | null | undefined) => String(v ?? '').replace(/\D/g, '').replace(/^0+/, '');

/**
 * UCs do agrupamento que não têm página no arquivo. A fatura delas não está no borderô, então
 * reenviar o mesmo arquivo não muda nada: precisa vir de outro lugar (fatura individual) ou a UC
 * ser dispensada do mês. Foi o caso da UC 97967501209 (Assistência Social, SET/2026).
 */
export function ucsForaDoBordero<T extends { numero_uc: string; outro_numero_uc?: string | null }>(
  ucsDoAgrupamento: T[],
  paginas: Pick<PaginaBordero, 'uc_norm'>[],
): T[] {
  const noArquivo = new Set(paginas.map((p) => p.uc_norm).filter(Boolean) as string[]);
  return ucsDoAgrupamento.filter((u) => {
    const numeros = [soDigitosSemZeros(u.numero_uc), soDigitosSemZeros(u.outro_numero_uc)].filter(Boolean);
    return numeros.length > 0 && !numeros.some((n) => noArquivo.has(n));
  });
}

/** Roda `fn` em todos os itens, no máximo `limite` ao mesmo tempo, parando de puxar novos
 *  itens quando `parar()` vira true (os que já começaram terminam). */
export async function emFila<T>(itens: T[], limite: number, fn: (item: T) => Promise<void>, parar: () => boolean = () => false) {
  let proximo = 0;
  const trabalhador = async () => {
    while (proximo < itens.length && !parar()) {
      const item = itens[proximo++];
      await fn(item);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limite, itens.length)) }, trabalhador));
}
